/** Only load notifications from the last N days (reduces crash risk after long idle). */
export const NOTIFICATION_LOOKBACK_DAYS = 5;

/**
 * Max geofence entry/exit notifications in the UI (from scheduled_geofence_analysis).
 * Older geofence items are dropped on each snapshot — only the latest N remain (newest first).
 */
export const NOTIFICATION_MAX_GEOFENCE_IN_UI = 30;

/** Max LG Band SOS notifications in the UI (newest first). */
export const NOTIFICATION_MAX_SOS_IN_UI = 10;

/** Max offline-device alerts in the UI (newest offline timestamp first). */
export const NOTIFICATION_MAX_OFFLINE_IN_UI = 20;

/** Dedicated Firestore query limit for offline-only listener (matches backend prune cap). */
export const NOTIFICATION_OFFLINE_FIRESTORE_QUERY_LIMIT = 20;

/**
 * Firestore read cap for geofence entry/exit (newest first).
 * Offline alerts use a separate query so geofence rows do not crowd them out.
 */
export const NOTIFICATION_FIRESTORE_QUERY_LIMIT = 50;

/** Legacy safety cap for client-side addNotification (geofencing service). */
export const NOTIFICATION_MAX_IN_MEMORY = 60;

/**
 * Geofence entry/exit alerts come from Cloud Functions → Firestore listener.
 * Client-side geofencing was duplicating alerts and could loop via store.subscribe.
 */
export const ENABLE_CLIENT_GEOFENCE_NOTIFICATIONS = false;

export function getNotificationLookbackDate(days = NOTIFICATION_LOOKBACK_DAYS) {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}

/**
 * Normalize notification timestamp to epoch seconds for comparison.
 */
export function getNotificationEpochSeconds(notification) {
  const ts = notification?.timestamp;
  if (!ts) return null;
  if (typeof ts === 'object' && ts.seconds != null) {
    return typeof ts.seconds === 'number' ? ts.seconds : Number(ts.seconds);
  }
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor(date.getTime() / 1000);
}

export function isNotificationWithinLookback(notification, lookbackDate) {
  const epoch = getNotificationEpochSeconds(notification);
  if (epoch == null) return true;
  return epoch >= Math.floor(lookbackDate.getTime() / 1000);
}

/** Geofence analyzer writes entry/exit (and category vehicle_geofence). */
export function isGeofenceCloudNotification(notification) {
  const eventType = (notification?.eventType || '').toLowerCase();
  if (eventType === 'entry' || eventType === 'exit') return true;
  const category = (notification?.category || '').toLowerCase();
  return category === 'vehicle_geofence';
}

export function isOfflineCloudNotification(notification) {
  return (notification?.eventType || '').toLowerCase() === 'offline';
}

/** LG Band SOS: persisted in `notifications` (eventType sos, category lg_band_sos) like entry/exit. */
export function isSosCloudNotification(notification) {
  if ((notification?.eventType || '').toLowerCase() === 'sos') return true;
  return (notification?.category || '').toLowerCase() === 'lg_band_sos';
}

/** Newest notification timestamp first (offline episode / alert time). */
export function sortNotificationsByTimestampDesc(notifications) {
  return [...notifications].sort((a, b) => {
    const ea = getNotificationEpochSeconds(a) ?? 0;
    const eb = getNotificationEpochSeconds(b) ?? 0;
    return eb - ea;
  });
}

/** Map a Firestore notification document snapshot to a Redux-friendly row. */
export function mapNotificationDocSnap(docSnap, lookbackDate) {
  const data = docSnap.data();
  // Firestore Timestamps are not serialisable for Redux: flatten every one we carry (timestamp,
  // createdAt from the geofence analyzer and the LG Band SOS writer).
  const toPlainTs = (v) => (v && typeof v === 'object' && v.seconds != null ? { seconds: v.seconds, nanoseconds: v.nanoseconds } : (v == null ? null : v));
  const serialized = {
    ...data,
    timestamp: data.timestamp ? toPlainTs(data.timestamp) : null,
  };
  if (data.createdAt !== undefined) serialized.createdAt = toPlainTs(data.createdAt);
  delete serialized.isRead;
  const row = { id: docSnap.id, ...serialized };
  if (lookbackDate != null && !isNotificationWithinLookback(row, lookbackDate)) return null;
  return row;
}

/**
 * Build UI list from separate geofence + offline query results (newest first, capped).
 */
export function buildNotificationListFromQueries(
  geofenceNotifications,
  offlineNotifications,
  sosNotifications = [],
  maxGeofence = NOTIFICATION_MAX_GEOFENCE_IN_UI,
  maxOffline = NOTIFICATION_MAX_OFFLINE_IN_UI,
  maxSos = NOTIFICATION_MAX_SOS_IN_UI
) {
  const sos = sortNotificationsByTimestampDesc(
    (sosNotifications || []).filter(isSosCloudNotification)
  ).slice(0, maxSos);
  const geofence = sortNotificationsByTimestampDesc(
    geofenceNotifications.filter(isGeofenceCloudNotification)
  ).slice(0, maxGeofence);
  const offline = sortNotificationsByTimestampDesc(
    offlineNotifications.filter(isOfflineCloudNotification)
  ).slice(0, maxOffline);
  return [...sos, ...geofence, ...offline];
}

/**
 * Build UI list: latest N geofence + latest N offline (single combined array).
 */
export function buildNotificationListForUi(
  permissionFiltered,
  maxGeofence = NOTIFICATION_MAX_GEOFENCE_IN_UI,
  maxOffline = NOTIFICATION_MAX_OFFLINE_IN_UI
) {
  return buildNotificationListFromQueries(
    permissionFiltered.filter(isGeofenceCloudNotification),
    permissionFiltered.filter(isOfflineCloudNotification),
    permissionFiltered.filter(isSosCloudNotification),
    maxGeofence,
    maxOffline
  );
}
